import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { usePublicSettings } from "@/lib/settings";
import { Loader2, CheckCircle2, Package, Truck, AlertCircle } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cart = useCart();
  const settings = usePublicSettings();
  const [state, setState] = useState({ loading: true, paid: false, expired: false, order: null });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, expired: true, order: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const { payment_status, session_status, order } = res.data;
        if (payment_status === "paid") {
          setState({ loading: false, paid: true, expired: false, order });
          if (!cleared.current) { cart.clear(); cleared.current = true; }
          return;
        }
        if (session_status === "expired") {
          setState({ loading: false, paid: false, expired: true, order });
          return;
        }
        if (attempts >= 8) {
          setState({ loading: false, paid: false, expired: false, order });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 5) {
          setState({ loading: false, paid: false, expired: false, order: null });
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        {state.loading ? (
          <div className="surface-card p-12 text-center" data-testid="checkout-loading">
            <Loader2 className="size-10 text-spark-teal animate-spin mx-auto mb-4" />
            <h1 className="font-display uppercase tracking-tight text-2xl font-bold">Confirming payment…</h1>
            <p className="text-sm text-spark-slate mt-2">Just a moment while Stripe verifies the transaction.</p>
          </div>
        ) : state.paid ? (
          <div className="surface-card p-8 sm:p-12" data-testid="checkout-success">
            <div className="size-14 rounded-full bg-spark-yellow flex items-center justify-center mb-5">
              <CheckCircle2 className="size-7 text-spark-ink" />
            </div>
            <p className="label-mono text-spark-teal">Payment successful</p>
            <h1 className="font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold text-spark-ink mt-1">Thanks, {state.order?.customer_name?.split(" ")[0]}!</h1>
            <p className="text-spark-slate mt-3">Your order <span className="font-display uppercase tracking-wider font-bold text-spark-teal">{state.order?.order_number}</span> is confirmed. We've emailed a receipt to <span className="font-semibold">{state.order?.customer_email}</span>.</p>

            <ul className="mt-6 divide-y divide-spark-line border border-spark-line rounded-md overflow-hidden">
              {state.order?.items?.map((it, i) => (
                <li key={i} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
                  <span className="text-spark-ink truncate">× {it.quantity}  {it.name}</span>
                  <span className="font-display uppercase tracking-tight font-bold tabular-nums">{money(it.price * it.quantity)}</span>
                </li>
              ))}
              <li className="bg-spark-teal text-white px-4 py-3 flex justify-between">
                <span className="label-mono text-spark-yellow">Paid</span>
                <span className="font-display uppercase tracking-tight text-2xl font-bold tabular-nums">{money(state.order?.total)}</span>
              </li>
            </ul>

            <div className="mt-6 p-4 rounded-md bg-spark-floor border border-spark-line flex items-start gap-3">
              {state.order?.fulfilment === "shipping" ? (
                <><Truck className="size-5 text-spark-teal shrink-0 mt-0.5" /><p className="text-sm text-spark-ink">We'll dispatch your parcel within 1 working day and email tracking. Average delivery is 2–4 working days NZ-wide.</p></>
              ) : (
                <><Package className="size-5 text-spark-teal shrink-0 mt-0.5" /><p className="text-sm text-spark-ink">Your order is ready for pickup{settings.address_line ? <> at <strong>{settings.address_line}</strong></> : null}. We'll text you when to come grab it.</p></>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/shop" className="btn-primary">Keep shopping</Link>
              <Link to="/" className="btn-outline-ink">Back to home</Link>
            </div>
          </div>
        ) : state.expired ? (
          <div className="surface-card p-8 text-center">
            <AlertCircle className="size-10 text-orange-500 mx-auto mb-3" />
            <h1 className="font-display uppercase tracking-tight text-2xl font-bold">Checkout expired</h1>
            <p className="text-sm text-spark-slate mt-2">The session timed out before payment was completed.</p>
            <Link to="/shop/cart" className="btn-primary mt-5">Back to cart</Link>
          </div>
        ) : (
          <div className="surface-card p-8 text-center">
            <AlertCircle className="size-10 text-orange-500 mx-auto mb-3" />
            <h1 className="font-display uppercase tracking-tight text-2xl font-bold">Still processing…</h1>
            <p className="text-sm text-spark-slate mt-2">Your payment is being processed. You'll receive an email once it's confirmed.</p>
            <Link to="/" className="btn-primary mt-5">Back to home</Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
